'use client';

import { useEffect } from 'react';
import { usePathname } from '@/i18n/routing';
import { useCampaignConfigs } from '@/hooks/useCampaignConfigs';
import { getWhatsNewAcknowledgePathsByIds } from '@/config/whats-new-updates';
import { acknowledgeAnnouncementCampaign } from '@/lib/announcement-modal-state';
import { sendGAEvent } from '@/utils/analytics';

function normalizePath(pathname = '/') {
  if (!pathname || pathname === '/') return '/';
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

function matchesPath(pathname, targetPath) {
  const normalizedPathname = normalizePath(pathname);
  const normalizedTargetPath = normalizePath(targetPath);

  return (
    normalizedPathname === normalizedTargetPath
    || normalizedPathname.startsWith(`${normalizedTargetPath}/`)
  );
}

export default function AnnouncementAcknowledgementTracker() {
  const pathname = usePathname();
  const {
    announcementModal,
    hasResolved,
  } = useCampaignConfigs();

  useEffect(() => {
    if (!hasResolved || !announcementModal?.enabled || !announcementModal?.campaignId) {
      return;
    }

    const normalizedPathname = normalizePath(pathname);
    let ackReason = null;

    if (normalizedPathname === '/whats-new') {
      ackReason = 'whats_new_visit';
    } else {
      const acknowledgePaths = getWhatsNewAcknowledgePathsByIds(announcementModal.featuredUpdateIds);
      if (acknowledgePaths.some((targetPath) => matchesPath(normalizedPathname, targetPath))) {
        ackReason = 'landing_visit';
      }
    }

    if (!ackReason) {
      return;
    }

    const result = acknowledgeAnnouncementCampaign({
      campaignId: announcementModal.campaignId,
      reason: ackReason,
    });

    if (result.changed) {
      sendGAEvent('announcement_modal_acknowledged', {
        campaign_id: announcementModal.campaignId,
        ack_reason: ackReason,
        dismiss_count: result.entry.dismissCount,
        path: normalizedPathname,
      });
    }
  }, [
    announcementModal?.campaignId,
    announcementModal?.enabled,
    announcementModal?.featuredUpdateIds,
    hasResolved,
    pathname,
  ]);

  return null;
}
